import { Helmet } from "react-helmet-async";

const SITE_NAME = "LicitX";
const SITE_URL = "https://licitx.cl";
const DEFAULT_IMAGE = `${SITE_URL}/licitx-logo-512.png`;

const absoluteUrl = (pathOrUrl = "/") => {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    const normalized = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
    return `${SITE_URL}${normalized === "/" ? "/" : normalized.replace(/\/$/, "")}`;
};

const safeJsonLd = (schema) => JSON.stringify(schema).replace(/</g, "\\u003c");

const buildPublicSeoValues = ({ title, path = "/", image = DEFAULT_IMAGE, imageAlt }) => {
    const canonical = absoluteUrl(path);
    const imageUrl = absoluteUrl(image);
    const socialTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    return {
        canonical,
        imageUrl,
        socialTitle,
        imageAlt: imageAlt || socialTitle,
    };
};

export default function PublicSeo({
    title,
    description,
    path = "/",
    image = DEFAULT_IMAGE,
    imageAlt,
    type = "website",
    robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    schemas = [],
    article = null,
}) {
    const seo = buildPublicSeoValues({ title, path, image, imageAlt });
    const schemaList = (Array.isArray(schemas) ? schemas : [schemas]).filter(Boolean);

    return (
        <Helmet>
            <html lang="es-CL" />
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="robots" content={robots} />
            <meta name="googlebot" content={robots} />
            <link rel="canonical" href={seo.canonical} />

            <meta property="og:type" content={type} />
            <meta property="og:locale" content="es_CL" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={seo.socialTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={seo.canonical} />
            <meta property="og:image" content={seo.imageUrl} />
            <meta property="og:image:alt" content={seo.imageAlt} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seo.socialTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={seo.imageUrl} />
            <meta name="twitter:image:alt" content={seo.imageAlt} />

            {article?.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
            {article?.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
            {(article?.tags || []).map((tag) => <meta key={tag} property="article:tag" content={tag} />)}

            {schemaList.map((schema, index) => (
                <script key={`${schema["@type"] || "schema"}-${index}`} type="application/ld+json">
                    {safeJsonLd(schema)}
                </script>
            ))}
        </Helmet>
    );
}

export { SITE_NAME, SITE_URL, DEFAULT_IMAGE, absoluteUrl, buildPublicSeoValues, safeJsonLd };
